import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../utils/apiBase';
import { useAuth } from './AuthContext';
import { isAppAdmin, isManagerOrAdmin as isManagerOrAdminHelper, isCuratorOrAbove } from '../utils/roles';
import staffService from '../services/staffService';
import { can as canHelper, sectionCount as sectionCountHelper } from '../utils/staffCapabilities';

const UserContext = createContext(null);

const API_URL = API_BASE;

export function UserProvider({ children }) {
  const { user: authUser, isAuthenticated, getAuthHeader, isLoading: authLoading } = useAuth();

  const [profile, setProfile] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [escrowBalance, setEscrowBalance] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetchedProfile, setHasFetchedProfile] = useState(false);
  const [error, setError] = useState(null);
  // The admin-panel key: { isStaff, isSuperadmin, status, roleKey, presetWord, grants, ... }.
  // Fetched once per session; `null` until it lands (the shell shows LOADING…).
  const [staff, setStaff] = useState(null);
  // PREVIEW AS: a superadmin looking at the panel through someone else's key. Client-side
  // only — it swaps what `can()` answers and nothing else, calls no write API, and is
  // dropped the moment EXIT PREVIEW is pressed or the tab is reloaded.
  const [previewStaff, setPreviewStaff] = useState(null);

  const refreshStaff = useCallback(async () => {
    const r = await staffService.me();
    setStaff(r.success ? r.data : { isStaff: false });
  }, []);

  // Fetch user profile
  const fetchProfile = useCallback(async () => {
    if (!isAuthenticated) {
      setProfile(null);
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/profiles/me`, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 404) {
        // Profile doesn't exist yet, that's okay
        setProfile(null);
        return null;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }

      const result = await response.json();
      const profileData = result.data || result;
      setProfile(profileData);
      return profileData;
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      setError(err.message);
      return null;
    } finally {
      setIsLoading(false);
      setHasFetchedProfile(true);
    }
  }, [isAuthenticated, getAuthHeader]);

  // Fetch wallet balance
  const fetchWallet = useCallback(async () => {
    if (!isAuthenticated) {
      setWalletBalance(null);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/wallets/me`, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const result = await response.json();
        const data = result.data || result;
        setWalletBalance(data.balance);
        setEscrowBalance(data.escrowBalance || 0);
      }
    } catch (err) {
      console.error('Failed to fetch wallet:', err);
    }
  }, [isAuthenticated, getAuthHeader]);

  // Refresh wallet balance
  const refreshWallet = useCallback(() => fetchWallet(), [fetchWallet]);

  // Update user profile
  const updateProfile = useCallback(async (profileData) => {
    if (!isAuthenticated) {
      return { success: false, error: 'Not authenticated' };
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/profiles/me`, {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || errorData.message || 'Failed to update profile');
      }

      const result = await response.json();
      const updatedProfile = result.data || result;
      setProfile(updatedProfile);
      return { success: true, profile: updatedProfile };
    } catch (err) {
      console.error('Failed to update profile:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, getAuthHeader]);

  // Update user mode
  const updateMode = useCallback(async (mode) => {
    if (!isAuthenticated) {
      return { success: false, error: 'Not authenticated' };
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/users/me/mode`, {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mode }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || errorData.message || 'Failed to update mode');
      }

      const result = await response.json();
      return { success: true, user: result.data || result };
    } catch (err) {
      console.error('Failed to update mode:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, getAuthHeader]);

  // Upload profile avatar
  const uploadAvatar = useCallback(async (file) => {
    if (!isAuthenticated) {
      return { success: false, error: 'Not authenticated' };
    }

    setIsLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/profiles/me/avatar`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || errorData.message || 'Failed to upload avatar');
      }

      const result = await response.json();
      const data = result.data || result;
      setProfile(prev => prev ? { ...prev, avatarUrl: data.avatarUrl } : null);
      return { success: true, avatarUrl: data.avatarUrl };
    } catch (err) {
      console.error('Failed to upload avatar:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, getAuthHeader]);

  // Delete user account
  const deleteAccount = useCallback(async () => {
    if (!isAuthenticated) {
      return { success: false, error: 'Not authenticated' };
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/users/me`, {
        method: 'DELETE',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.error?.message || errorData.message || 'Failed to delete account';
        return { success: false, error: message };
      }

      setProfile(null);
      return { success: true };
    } catch (err) {
      console.error('Failed to delete account:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, getAuthHeader]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Sync with auth state - fetch profile and wallet when authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchProfile();
      fetchWallet();
      refreshStaff();
    } else if (!isAuthenticated) {
      setProfile(null);
      setWalletBalance(null);
      setEscrowBalance(null);
      setHasFetchedProfile(false);
      setStaff(null);
    }
  }, [isAuthenticated, authLoading, fetchProfile, fetchWallet, refreshStaff]);

  // Computed values
  const currentUser = authUser ? {
    ...authUser,
    profile,
  } : null;

  const role = authUser?.role || null;
  // Treat superadmin as a strict superset of admin so the Portal owner role
  // automatically unlocks every admin-gated UI (Admin Dashboard, Settings
  // tab, role assignment, etc.) without a parallel set of checks.
  const isAdmin = isAppAdmin(role);
  const isManager = role === 'manager';
  const isCurator = role === 'curator';
  const canCurate = isCuratorOrAbove(role);
  const isManagerOrAdmin = isManagerOrAdminHelper(role);

  // Staff access. `can(capability, verb)` is the only gate the admin UI needs — and while
  // a preview is on it answers for the person being previewed, so every rail entry, row
  // action and header button in the panel is already right without a second code path.
  const effectiveStaff = previewStaff || staff;
  const isStaff = !!effectiveStaff?.isStaff && effectiveStaff.status === 'active';
  const isSuperadmin = !!effectiveStaff?.isSuperadmin;
  const can = useCallback((capability, verb) => canHelper(effectiveStaff, capability, verb), [effectiveStaff]);
  const staffSectionCount = sectionCountHelper(effectiveStaff);

  /** Look at the panel as this staff row. `null` ends the preview. */
  const previewAs = useCallback((row) => setPreviewStaff(row ? { ...row, isStaff: true } : null), []);

  // The server decides who gets the planner (`planner_audience`); /profiles/me carries the
  // answer so the create menu can offer START A CLUB instead without a call of its own.
  // Undefined while the profile is still loading — treat that as "not yet", never as yes.
  const canUsePlanner = profile?.canUsePlanner === true;

  const value = {
    currentUser,
    profile,
    canUsePlanner,
    role,
    isAdmin,
    isManager,
    isCurator,
    canCurate,
    isManagerOrAdmin,
    staff: effectiveStaff,
    staffLoaded: staff !== null,
    isStaff,
    isSuperadmin,
    can,
    staffSectionCount,
    refreshStaff,
    previewStaff,
    previewAs,
    walletBalance,
    escrowBalance,
    isLoading: isLoading || authLoading || (isAuthenticated && !hasFetchedProfile),
    error,
    fetchProfile,
    updateProfile,
    updateMode,
    uploadAvatar,
    deleteAccount,
    clearError,
    refreshWallet,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

export default UserContext;
